import React, { useState, useEffect } from 'react';
import { useUploadStatus } from '../../../utils/UploadStatusContext';
import './UploadQueue.css';

const UploadQueue = ({ isCollapsed = false }) => {
  const { uploadStatus, cancelUploadItem, cancelAllUploads } = useUploadStatus();
  const { queueItems = [] } = uploadStatus;
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDismissed, setIsDismissed] = useState(() => {
    const stored = localStorage.getItem('uploadQueueDismissed');
    return stored === 'true';
  });
  const hasQueueItems = queueItems.length > 0;

  useEffect(() => {
    if (hasQueueItems) {
      setIsDismissed(false);
      localStorage.setItem('uploadQueueDismissed', 'false');
    }
  }, [hasQueueItems]);

  // Always render the bar in the sidebar — hide only the expanded popup when dismissed

  const handleCancel = async (itemId) => {
    const result = await cancelUploadItem(itemId);
    // If cancel failed (item not found on server), it will be cleaned up by SSE update
    // or auto-removed when queue clears
  };

  const handleCancelAll = async () => {
    const result = await cancelAllUploads();
    if (result.success) {
      console.log(`Cancelled ${result.cancelledCount} of ${result.totalAttempted} uploads`);
    }
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const handleClosePopup = () => {
    if (activeCount === 0) {
      setIsExpanded(false);
      setIsDismissed(true);
      localStorage.setItem('uploadQueueDismissed', 'true');
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'pending':
        return 'status-pending';
      case 'processing':
        return 'status-processing';
      case 'completed':
        return 'status-completed';
      case 'error':
        return 'status-error';
      case 'cancelled':
        return 'status-cancelled';
      default:
        return '';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return '○';
      case 'processing':
        return '◐';
      case 'completed':
        return '✓';
      case 'error':
        return '✕';
      case 'cancelled':
        return '■';
      default:
        return '';
    }
  };

  const truncateFileName = (fileName, maxLength = 30) => {
    if (fileName.length <= maxLength) return fileName;
    const extension = fileName.split('.').pop();
    const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.'));
    const truncated = nameWithoutExt.substring(0, maxLength - extension.length - 4) + '...';
    return `${truncated}.${extension}`;
  };

  // Reverse queue items so oldest uploads appear at top
  const reversedQueueItems = hasQueueItems ? [...queueItems].reverse() : [];

  // Count active uploads (not completed/error)
  const activeCount = queueItems.filter(
    item => item.status === 'pending' || item.status === 'processing'
  ).length;

  return (
      <div className={`upload-queue-container${isCollapsed ? ' collapsed' : ''}`}>
      {/* Collapsed Bar - Always visible */}
      <div className="upload-queue-bar" onClick={toggleExpanded}>
        <div className="bar-content">
          <span className="bar-icon">↑</span>
          <span className="bar-text">
            Uploads {activeCount > 0 && `(${activeCount})`}
          </span>
          <span className="queue-count-badge">{queueItems.length}</span>
        </div>
        <div className="expand-icon">{isExpanded ? '▼' : '▲'}</div>
      </div>

      {/* Expanded Popup */}
        {isExpanded && (
        <div className="upload-queue-popup">
          <div className="upload-queue-header">
            <h3>Upload-Warteschlange</h3>
            <div style={{ display: 'flex', gap: '8px' }}>
              {activeCount > 0 && (
                <button
                  className="cancel-all-btn"
                  onClick={handleCancelAll}
                  aria-label="Alle abbrechen"
                  title={`Alle ${activeCount} aktiven Uploads abbrechen`}
                >
                  Alle abbrechen
                </button>
              )}
              <button
                className="close-btn"
                onClick={handleClosePopup}
                disabled={activeCount > 0}
                aria-label="Schließen"
                title={
                  activeCount > 0
                    ? 'Schließen erst möglich, wenn alle Uploads abgeschlossen sind'
                    : 'Warteschlange schließen'
                }
              >
                ✕
              </button>
            </div>
          </div>
            <div className="upload-queue-items">
              {hasQueueItems ? (
                reversedQueueItems.map((item) => (
                  <div key={item.id} className={`queue-item ${getStatusClass(item.status)}`}>
                    <div className="queue-item-header">
                      <span className="status-icon">{getStatusIcon(item.status)}</span>
                      <span className="file-name" title={item.fileName}>
                        {truncateFileName(item.fileName)}
                      </span>
                      <button
                        className="cancel-btn"
                        onClick={() => handleCancel(item.id)}
                        title={
                          item.status === 'completed' ||
                          item.status === 'error' ||
                          item.status === 'cancelled'
                            ? 'Entfernen'
                            : 'Abbrechen'
                        }
                        aria-label={
                          item.status === 'completed' ||
                          item.status === 'error' ||
                          item.status === 'cancelled'
                            ? 'Aus Warteschlange entfernen'
                            : 'Upload abbrechen'
                        }
                      >
                        ✕
                      </button>
                    </div>
                    {item.status === 'processing' && (
                      <>
                        {item.stepLabel && (
                          <div className="step-label">{item.stepLabel}</div>
                        )}
                        <div className="progress-bar-container">
                          <div
                            className="progress-bar"
                            style={{ width: `${item.progress || 0}%` }}
                            role="progressbar"
                            aria-valuenow={item.progress || 0}
                            aria-valuemin="0"
                            aria-valuemax="100"
                          />
                        </div>
                      </>
                    )}
                    {item.error && (
                      <div className="error-message" title={item.error}>
                        {item.error}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className="upload-queue-empty">
                  Keine Uploads in der Warteschlange
                </div>
              )}
            </div>
        </div>
      )}
    </div>
  );
};

export default UploadQueue;
